import {
  type Filter,
  type FilterCondition,
  decodeTableState,
  EMPTY_TABLE_STATE,
  encodeTableState,
  type SavedViewEntity,
  type TableState,
} from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SAVED_VIEWS_QUERY } from '../components/record-table/views-menu.tsx';
import { api } from './client.ts';
import { useSession } from './session.tsx';
import { LAST_VIEW_MAX, readSettings, writeSettings } from './settings.ts';

// Bounds storage; each organisation has a handful of lists.
const LAST_VIEWS_KEPT = 50;

/** Remembers a list's last state for the user; the empty state removes the entry. */
function storeLastView(userId: string, key: string, encoded: string) {
  const settings = readSettings(userId);
  const { [key]: _old, ...rest } = settings.lastViews;
  // An empty state is remembered too: switching every filter off is a choice, not "nothing yet".
  const keep = encoded.length <= LAST_VIEW_MAX;
  const entries = Object.entries(keep ? { ...rest, [key]: encoded } : rest);
  writeSettings(userId, {
    ...settings,
    lastViews: Object.fromEntries(entries.slice(-LAST_VIEWS_KEPT)),
  });
}

/**
 * A list's view state (RELAY-1038): filter, search, layout (table or board) and card fields in
 * ONE `TableState`, so a saved view, a link and the screen are the same list whichever layout
 * shows it. The caller says where the encoded state lives — the URL for a page, component
 * state for a board embedded in a page — through `encoded` and `write`.
 *
 * A page passes `remember` (the list's name) so leaving and coming back through the sidebar
 * keeps the list as it was: its last state is kept per user and organisation (stage and
 * pipeline ids are the organisation's) in the user's settings. The list opens on, in order:
 * the state in `encoded` (a link, which then becomes the remembered one), the remembered state,
 * the user's default saved view (`entity`; `null` for a list without saved views), `initial`
 * (e.g. "my open jobs"). Once anything is chosen the state is always written, even when empty,
 * so turning every quick filter off is not mistaken for "nothing chosen yet" and silently reset
 * to `initial`.
 */
export function useListView(opts: {
  entity: SavedViewEntity | null;
  encoded: string | undefined;
  write: (encoded: string) => void;
  initial?: TableState | undefined;
  remember?: string;
}) {
  const { entity, encoded, write, initial, remember } = opts;
  const session = useSession();
  const userId = session.me.user.id;
  const memoryKey = remember && `${session.me.organisation.id}:${remember}`;
  // Read only while nothing is in `encoded`: that is when it is restored.
  const last = useMemo(
    () => (memoryKey && !encoded ? readSettings(userId).lastViews[memoryKey] : undefined),
    [memoryKey, encoded, userId],
  );
  const lastState = useMemo(() => decodeTableState(last), [last]);
  const stored = useMemo(() => decodeTableState(encoded), [encoded]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const views = useQuery({
    queryKey: SAVED_VIEWS_QUERY(entity ?? 'none'),
    queryFn: () => (entity ? api.savedViews.list(entity) : []),
    enabled: entity !== null,
  });
  const setState = useCallback(
    (next: TableState, viewId: string | null | undefined = undefined) => {
      if (viewId !== undefined) setActiveViewId(viewId);
      const nextEncoded = encodeTableState(next);
      // Before `write`: a caller that drops the empty state from the URL must not get the old one back.
      if (memoryKey) storeLastView(userId, memoryKey, nextEncoded);
      write(nextEncoded);
    },
    [write, memoryKey, userId],
  );
  const firstRun = useRef(true);
  useEffect(() => {
    const first = firstRun.current;
    firstRun.current = false;
    if (!memoryKey) return;
    // A link's state is remembered once, on open; later changes go through `setState`.
    if (encoded) {
      if (first && stored) storeLastView(userId, memoryKey, encoded);
      return;
    }
    if (last && lastState) write(last);
  }, [memoryKey, encoded, stored, last, lastState, userId, write]);
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (appliedDefault.current || !views.data) return;
    appliedDefault.current = true;
    if (encoded || lastState) return;
    const def = views.data.find((v) => v.isDefault);
    // Not remembered: the next visit applies the default (and names it) again.
    if (def) {
      setActiveViewId(def.id);
      write(encodeTableState(def.state));
    }
  }, [views.data, encoded, lastState, write]);
  return {
    state: stored ?? lastState ?? initial ?? EMPTY_TABLE_STATE,
    setState,
    activeViewId,
  };
}

/** The flat conditions a filter bar edits: a lone condition, or the conditions of a top-level AND. */
export function flatConditions(filter: Filter | undefined): FilterCondition[] {
  if (!filter) return [];
  if ('field' in filter) return [filter];
  if ('and' in filter) return filter.and.filter((c): c is FilterCondition => 'field' in c);
  return [];
}

/** Parts ANDed into one filter: nothing is no filter, one part is itself. */
export function andFilter(parts: readonly Filter[]): Filter | undefined {
  if (parts.length === 0) return undefined;
  return parts.length === 1 ? parts[0] : { and: [...parts] };
}
